import { FormControl, FormLabel, HStack, IconButton, Input, Radio, RadioGroup, Select, Stack, Text, VStack } from '@chakra-ui/react';
import { MdAdd, MdDelete } from 'react-icons/md';
import { useWatch } from 'react-hook-form';
import { TaskType } from '@/lib/sdk/chatbot/types';
import type { MasterDataFieldDefinition } from '@/lib/sdk/master-data-field-definitions/types';

// DATA_SOURCES is the picker's offering for NEW tasks. The four legacy
// dataset-specific sources (nearby_stores/regions/region_stores/brand_stores) are
// now fully expressible via master_data's dataset + filterTask (browse/filter),
// mode="nearby" (geo search), and mode="distinct" (parent_key value list) — see
// internal/workflow/datasources/master_data.go. They're intentionally left off this
// list so new tasks aren't offered a choice that's strictly redundant with Master
// Data, but their config blocks below are kept unchanged and still render — the
// Select injects the current value as an extra option (via LEGACY_DATA_SOURCE_LABELS)
// when it's one of these — so existing tasks already configured with one keep
// working and stay editable.
const DATA_SOURCES = [
  { value: 'master_data', label: 'Master Data' },
  { value: 'team', label: 'Team Member' },
];

const LEGACY_DATA_SOURCE_LABELS: Record<string, string> = {
  nearby_stores: 'Nearby Stores (legacy)',
  regions: 'Regions/Routes (legacy)',
  region_stores: 'Stores by Region (legacy)',
  brand_stores: 'Brand Stores (legacy)',
};

const MASTER_DATA_MODES = [
  { value: '', label: 'Browse / Filter (default)' },
  { value: 'nearby', label: 'Nearby Search' },
  { value: 'distinct', label: 'Distinct Values' },
];

interface DataSourceConfigSectionProps {
  control: any;
  setValue: any;
  existingTasks: { id: string; name: string; index: number; type: string }[];
  currentTaskIndex?: number;
  /** A dynamic_choice/choice field renders as Meta's Dropdown component inside a
   * WhatsApp Flow (a single scrollable list, not the native channel's paginated
   * interactive list messages), and Dropdown supports up to 200 options without
   * images. On the native channel, results beyond 10 are split across multiple
   * list messages (10 rows each) — see platform/datasource.go's
   * nativeListMaxLimit/flowMaxLimit on the backend. */
  flowsEnabled?: boolean;
  /** Field definitions for the currently selected master_data dataset, used to let
   * Filter by Task match against a custom field instead of the default parent_key
   * column — see resolveFilterFields in internal/workflow/types/dynamic_choice.go. */
  masterDataFieldDefs?: MasterDataFieldDefinition[];
  /** Dataset names (categories plus datasets that have rows), offered as a dropdown for the
   * master_data source instead of free-text entry — same convention as Role,
   * Counter, Text, and Location Check's dataset pickers. */
  masterDataDatasets?: string[];
}

const NATIVE_MAX_RESULTS = 50;
const FLOW_MAX_RESULTS = 200;

export function DataSourceConfigSection({
  control,
  setValue,
  existingTasks,
  currentTaskIndex,
  flowsEnabled,
  masterDataFieldDefs = [],
  masterDataDatasets = [],
}: DataSourceConfigSectionProps) {
  const dataSourceConfig = useWatch({ control, name: 'dataSourceConfig' }) ?? {};
  const maxResults = flowsEnabled ? FLOW_MAX_RESULTS : NATIVE_MAX_RESULTS;

  const locationTasksBefore = existingTasks.filter(
    (t) =>
      (t.type === TaskType.LOCATION || t.type === TaskType.LOCATION_CHECK) &&
      (currentTaskIndex === undefined || t.index < currentTaskIndex)
  );

  const choiceTasksBefore = existingTasks.filter(
    (t) =>
      (t.type === TaskType.CHOICE || t.type === TaskType.DYNAMIC_CHOICE) &&
      (currentTaskIndex === undefined || t.index < currentTaskIndex)
  );

  const allTasksBefore = existingTasks.filter(
    (t) => currentTaskIndex === undefined || t.index < currentTaskIndex
  );

  return (
    <VStack spacing={4} align="stretch">
      <FormControl isRequired>
        <FormLabel fontSize="sm" color="text-secondary">Data Source</FormLabel>
        <Select
          placeholder="Select data source"
          bg="bg-surface"
          size="sm"
          value={dataSourceConfig.source ?? ''}
          onChange={(e) => setValue('dataSourceConfig.source', e.target.value)}
        >
          {dataSourceConfig.source && LEGACY_DATA_SOURCE_LABELS[dataSourceConfig.source] && (
            <option value={dataSourceConfig.source}>{LEGACY_DATA_SOURCE_LABELS[dataSourceConfig.source]}</option>
          )}
          {DATA_SOURCES.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </Select>
      </FormControl>

      {dataSourceConfig.source === 'nearby_stores' && (
        <VStack spacing={3} align="stretch">
          <Stack direction={{ base: 'column', sm: 'row' }} spacing={3}>
            <FormControl flex={1}>
              <FormLabel fontSize="sm" color="text-secondary">Location Task</FormLabel>
              <Select
                placeholder="Select location task"
                bg="bg-surface"
                size="sm"
                value={dataSourceConfig.locationTask !== undefined ? dataSourceConfig.locationTask : ''}
                onChange={(e) => {
                  const val = e.target.value;
                  setValue('dataSourceConfig.locationTask', val === '' ? undefined : parseInt(val, 10));
                }}
              >
                {locationTasksBefore.map((t) => (
                  <option key={t.id} value={t.index}>{t.name} (Task {t.index})</option>
                ))}
              </Select>
              <Text fontSize="xs" color="text-muted" mt={1}>Task that captures user location</Text>
            </FormControl>
            <FormControl flex={1}>
              <FormLabel fontSize="sm" color="text-secondary">Search Radius (km)</FormLabel>
              <Input
                type="number"
                placeholder="5"
                bg="bg-surface"
                size="sm"
                value={dataSourceConfig.radiusKm !== undefined ? dataSourceConfig.radiusKm : ''}
                onChange={(e) => {
                  const val = e.target.value;
                  setValue('dataSourceConfig.radiusKm', val === '' ? undefined : parseFloat(val));
                }}
              />
            </FormControl>
          </Stack>
          <FormControl>
            <FormLabel fontSize="sm" color="text-secondary">Dataset (optional)</FormLabel>
            <Input
              type="text"
              placeholder="store"
              bg="bg-surface"
              size="sm"
              value={dataSourceConfig.dataset ?? ''}
              onChange={(e) => setValue('dataSourceConfig.dataset', e.target.value || undefined)}
            />
            <Text fontSize="xs" color="text-muted" mt={1}>Master data dataset to search. Defaults to &quot;store&quot; if left blank.</Text>
          </FormControl>
        </VStack>
      )}

      {dataSourceConfig.source === 'regions' && (
        <FormControl>
          <FormLabel fontSize="sm" color="text-secondary">Dataset (optional)</FormLabel>
          <Input
            type="text"
            placeholder="store"
            bg="bg-surface"
            size="sm"
            value={dataSourceConfig.dataset ?? ''}
            onChange={(e) => setValue('dataSourceConfig.dataset', e.target.value || undefined)}
          />
          <Text fontSize="xs" color="text-muted" mt={1}>
            Master data dataset whose distinct &quot;Belongs To&quot; values are listed as regions. Defaults to &quot;store&quot; if left blank.
          </Text>
        </FormControl>
      )}

      {dataSourceConfig.source === 'region_stores' && (
        <VStack spacing={3} align="stretch">
          <FormControl>
            <FormLabel fontSize="sm" color="text-secondary">Region Task</FormLabel>
            <Select
              placeholder="Select region task"
              bg="bg-surface"
              size="sm"
              value={dataSourceConfig.regionTask !== undefined ? dataSourceConfig.regionTask : ''}
              onChange={(e) => {
                const val = e.target.value;
                setValue('dataSourceConfig.regionTask', val === '' ? undefined : parseInt(val, 10));
              }}
            >
              {choiceTasksBefore.map((t) => (
                <option key={t.id} value={t.index}>{t.name} (Task {t.index})</option>
              ))}
            </Select>
            <Text fontSize="xs" color="text-muted" mt={1}>Task that contains the region selection</Text>
          </FormControl>
          <FormControl>
            <FormLabel fontSize="sm" color="text-secondary">Dataset (optional)</FormLabel>
            <Input
              type="text"
              placeholder="store"
              bg="bg-surface"
              size="sm"
              value={dataSourceConfig.dataset ?? ''}
              onChange={(e) => setValue('dataSourceConfig.dataset', e.target.value || undefined)}
            />
            <Text fontSize="xs" color="text-muted" mt={1}>Master data dataset to filter by region. Defaults to &quot;store&quot; if left blank.</Text>
          </FormControl>
        </VStack>
      )}

      {dataSourceConfig.source === 'brand_stores' && (
        <FormControl>
          <FormLabel fontSize="sm" color="text-secondary">Dataset (optional)</FormLabel>
          <Input
            type="text"
            placeholder="brand_store"
            bg="bg-surface"
            size="sm"
            value={dataSourceConfig.dataset ?? ''}
            onChange={(e) => setValue('dataSourceConfig.dataset', e.target.value || undefined)}
          />
          <Text fontSize="xs" color="text-muted" mt={1}>Master data dataset to load brand stores from. Defaults to &quot;brand_store&quot; if left blank.</Text>
        </FormControl>
      )}

      {dataSourceConfig.source === 'master_data' && (
        <VStack spacing={3} align="stretch">
          <FormControl>
            <FormLabel fontSize="sm" color="text-secondary">Category</FormLabel>
            <Select
              placeholder="Select category"
              bg="bg-surface"
              size="sm"
              value={dataSourceConfig.dataset ?? ''}
              onChange={(e) => setValue('dataSourceConfig.dataset', e.target.value || undefined)}
              data-testid="wfv4-config-dynamic-choice-dataset-select"
            >
              {dataSourceConfig.dataset && !masterDataDatasets.includes(dataSourceConfig.dataset) && (
                <option value={dataSourceConfig.dataset}>{dataSourceConfig.dataset}</option>
              )}
              {masterDataDatasets.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </Select>
            <Text fontSize="xs" color="text-muted" mt={1}>Master data category to load options from</Text>
          </FormControl>
          <FormControl>
            <FormLabel fontSize="sm" color="text-secondary">Mode</FormLabel>
            <Select
              bg="bg-surface"
              size="sm"
              value={dataSourceConfig.mode ?? ''}
              onChange={(e) => {
                const val = e.target.value;
                setValue('dataSourceConfig.mode', val || undefined);
                // Modes are mutually exclusive with the default browse/filter fields.
                if (val) {
                  setValue('dataSourceConfig.filterTask', undefined);
                  setValue('dataSourceConfig.lookupFields', undefined);
                  setValue('dataSourceConfig.lookupField', undefined);
                  setValue('dataSourceConfig.fieldFilters', undefined);
                }
              }}
              data-testid="wfv4-config-master-data-mode-select"
            >
              {MASTER_DATA_MODES.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </Select>
          </FormControl>

          {dataSourceConfig.mode === 'nearby' && (
            <Stack direction={{ base: 'column', sm: 'row' }} spacing={3}>
              <FormControl flex={1}>
                <FormLabel fontSize="sm" color="text-secondary">Location Task</FormLabel>
                <Select
                  placeholder="Select location task"
                  bg="bg-surface"
                  size="sm"
                  value={dataSourceConfig.locationTask !== undefined ? dataSourceConfig.locationTask : ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    setValue('dataSourceConfig.locationTask', val === '' ? undefined : parseInt(val, 10));
                  }}
                >
                  {locationTasksBefore.map((t) => (
                    <option key={t.id} value={t.index}>{t.name} (Task {t.index})</option>
                  ))}
                </Select>
                <Text fontSize="xs" color="text-muted" mt={1}>Task that captures user location</Text>
              </FormControl>
              <FormControl flex={1}>
                <FormLabel fontSize="sm" color="text-secondary">Search Radius (km)</FormLabel>
                <Input
                  type="number"
                  placeholder="5"
                  bg="bg-surface"
                  size="sm"
                  value={dataSourceConfig.radiusKm !== undefined ? dataSourceConfig.radiusKm : ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    setValue('dataSourceConfig.radiusKm', val === '' ? undefined : parseFloat(val));
                  }}
                />
              </FormControl>
            </Stack>
          )}

          {dataSourceConfig.mode === 'distinct' && (
            <Text fontSize="xs" color="text-muted">
              Lists this dataset&apos;s distinct grouping values (Belongs To) with a row count for each, instead of
              individual rows.
            </Text>
          )}

          {dataSourceConfig.mode !== 'distinct' && (
            <FormControl>
              <FormLabel fontSize="sm" color="text-secondary">Answer Field</FormLabel>
              <Select
                bg="bg-surface"
                size="sm"
                value={dataSourceConfig.answerField ?? 'label'}
                onChange={(e) => setValue('dataSourceConfig.answerField', e.target.value || undefined)}
                data-testid="wfv4-config-dynamic-choice-answer-field-select"
              >
                <option value="key">Key</option>
                <option value="label">Label</option>
                {masterDataFieldDefs.map((def) => (
                  <option key={def.field_name} value={def.field_name}>
                    {def.label} ({def.field_name})
                  </option>
                ))}
              </Select>
              <Text fontSize="xs" color="text-muted" mt={1}>
                Field of the selected row saved as this task&apos;s answer when a choice is made. Defaults to Label.
              </Text>
            </FormControl>
          )}

          {!dataSourceConfig.mode && (
            <FormControl>
              <FormLabel fontSize="sm" color="text-secondary">Filter by Task (optional)</FormLabel>
              <Select
                placeholder="No filter (show all)"
                bg="bg-surface"
                size="sm"
                value={dataSourceConfig.filterTask !== undefined ? dataSourceConfig.filterTask : ''}
                onChange={(e) => {
                  const val = e.target.value;
                  setValue('dataSourceConfig.filterTask', val === '' ? undefined : parseInt(val, 10));
                }}
              >
                {allTasksBefore.map((t) => (
                  <option key={t.id} value={t.index}>{t.name} (Task {t.index})</option>
                ))}
              </Select>
              <Text fontSize="xs" color="text-muted" mt={1}>Filter options by a previous task's value</Text>
            </FormControl>
          )}
          {!dataSourceConfig.mode && dataSourceConfig.filterTask !== undefined && (() => {
            const currentMatchField = dataSourceConfig.lookupField
              ?? dataSourceConfig.lookupFields?.[0];
            const currentInList = !currentMatchField
              || currentMatchField === 'parent_key'
              || currentMatchField === 'key'
              || currentMatchField === 'label'
              || masterDataFieldDefs.some((def) => def.field_name === currentMatchField);
            const selectField = (val: string) => {
              setValue('dataSourceConfig.lookupField', val || undefined);
              setValue('dataSourceConfig.lookupFields', undefined);
            };
            return (
              <FormControl>
                <FormLabel fontSize="sm" color="text-secondary">Filter Match Field</FormLabel>
                <RadioGroup value={currentMatchField ?? ''} onChange={selectField}>
                  <Stack spacing={1}>
                    {!currentInList && (
                      <Radio value={currentMatchField} size="sm">
                        {currentMatchField} (not a defined field)
                      </Radio>
                    )}
                    <Radio value="parent_key" size="sm" data-testid="wfv4-config-dynamic-choice-lookup-field-parent-key">
                      Parent Key
                    </Radio>
                    <Radio value="key" size="sm" data-testid="wfv4-config-dynamic-choice-lookup-field-key">
                      Key
                    </Radio>
                    <Radio value="label" size="sm" data-testid="wfv4-config-dynamic-choice-lookup-field-label">
                      Label
                    </Radio>
                    {masterDataFieldDefs.map((def) => (
                      <Radio
                        key={def.field_name}
                        value={def.field_name}
                        size="sm"
                        data-testid={`wfv4-config-dynamic-choice-lookup-field-${def.field_name}`}
                      >
                        {def.label} ({def.field_name})
                      </Radio>
                    ))}
                  </Stack>
                </RadioGroup>
                <Text fontSize="xs" color="text-muted" mt={1}>
                  Field of this dataset the filter task's value must match. Defaults to Parent Key when none is selected.
                </Text>
              </FormControl>
            );
          })()}
          {!dataSourceConfig.mode && (() => {
            const rows: { field: string; value: string }[] = dataSourceConfig.fieldFilters ?? [];
            const setRows = (next: { field: string; value: string }[]) =>
              setValue('dataSourceConfig.fieldFilters', next.length > 0 ? next : undefined);
            const updateRow = (index: number, change: Partial<{ field: string; value: string }>) =>
              setRows(rows.map((row, i) => (i === index ? { ...row, ...change } : row)));
            return (
              <FormControl>
                <HStack justify="space-between" mb={2}>
                  <FormLabel fontSize="sm" color="text-secondary" mb={0}>Fixed Filters (optional)</FormLabel>
                  <IconButton
                    aria-label="Add fixed filter"
                    icon={<MdAdd />}
                    size="xs"
                    colorScheme="brand"
                    isDisabled={masterDataFieldDefs.length === 0}
                    onClick={() => setRows([...rows, { field: '', value: '' }])}
                    data-testid="wfv4-config-dynamic-choice-field-filter-add-btn"
                  />
                </HStack>
                <VStack spacing={2} align="stretch">
                  {rows.map((row, index) => (
                    <HStack key={index} spacing={2}>
                      <Select
                        placeholder="Select field"
                        size="sm"
                        bg="bg-surface"
                        flex={1}
                        value={row.field}
                        onChange={(e) => updateRow(index, { field: e.target.value })}
                        data-testid={`wfv4-config-dynamic-choice-field-filter-field-${index}`}
                      >
                        {row.field && !masterDataFieldDefs.some((def) => def.field_name === row.field) && (
                          <option value={row.field}>{row.field} (not a defined field)</option>
                        )}
                        {masterDataFieldDefs
                          .filter((def) => !rows.some((other, i) => i !== index && other.field === def.field_name))
                          .map((def) => (
                            <option key={def.field_name} value={def.field_name}>
                              {def.label} ({def.field_name})
                            </option>
                          ))}
                      </Select>
                      <Input
                        placeholder="e.g. PENDING"
                        size="sm"
                        bg="bg-surface"
                        flex={1}
                        value={row.value}
                        onChange={(e) => updateRow(index, { value: e.target.value })}
                        data-testid={`wfv4-config-dynamic-choice-field-filter-value-${index}`}
                      />
                      <IconButton
                        aria-label="Remove fixed filter"
                        icon={<MdDelete />}
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        onClick={() => setRows(rows.filter((_, i) => i !== index))}
                        data-testid={`wfv4-config-dynamic-choice-field-filter-remove-${index}`}
                      />
                    </HStack>
                  ))}
                </VStack>
                <Text fontSize="xs" color="text-muted" mt={1}>
                  {masterDataFieldDefs.length === 0
                    ? 'Define fields for this category under Manage Fields to filter on them.'
                    : 'Only list rows whose field equals the value (not case-sensitive), e.g. Status = PENDING. Rows without a value are not saved.'}
                </Text>
              </FormControl>
            );
          })()}
        </VStack>
      )}

      {dataSourceConfig.source && (
        <Stack direction={{ base: 'column', sm: 'row' }} spacing={3}>
          <FormControl flex={1}>
            <FormLabel fontSize="sm" color="text-secondary">Maximum Results</FormLabel>
            <Input
              type="number"
              placeholder="10"
              min={1}
              max={maxResults}
              bg="bg-surface"
              size="sm"
              value={dataSourceConfig.limit !== undefined ? dataSourceConfig.limit : ''}
              onChange={(e) => {
                const val = e.target.value;
                const num = parseInt(val, 10);
                setValue('dataSourceConfig.limit', val === '' ? undefined : Math.min(num, maxResults));
              }}
            />
            <Text fontSize="xs" color="text-muted" mt={1}>
              Maximum {maxResults}{flowsEnabled ? ' (Flows enabled)' : ''}
            </Text>
          </FormControl>
          <FormControl flex={1}>
            <FormLabel fontSize="sm" color="text-secondary">Empty Message</FormLabel>
            <Input
              type="text"
              placeholder="No results found"
              bg="bg-surface"
              size="sm"
              value={dataSourceConfig.emptyMessage ?? ''}
              onChange={(e) => setValue('dataSourceConfig.emptyMessage', e.target.value)}
            />
          </FormControl>
        </Stack>
      )}
    </VStack>
  );
}
